import React, { useState } from "react";
import { map } from "lodash";
import { useTranslation } from "react-i18next";
import moment from "moment";
import useQuery from "Hooks/query.hook";
import useSession from "Hooks/session.hook";
import useSearch from "Hooks/search.hook";
import orderStatusesMap from "Data/order-statuses.json";
import { ToTitleCase } from "Helpers/to-title-case";
import { DataTable } from "@life-line-lab/components-react";
import { Col, Row, Divider, Spin } from "antd";
import DataTableFilter from "Components/shared/data-table-filter";
import ReportFilters from "./report-filters";
import { getStatusStyle } from "Helpers/get-status-colours";
import { toCapitalisedSpaced } from "Helpers/to-capitalised-spaced";
import { LoadingOutlined } from "@ant-design/icons";
import { routeToOrderView } from "Helpers/route-order-view";
import { useHistory, useLocation } from "react-router-dom";
import {
  routeToBoxesView,
  routeToEnrouteView,
  routeToTrolleyView,
} from "Helpers/index";

const ParcelReportList = ({
  onFilter,
  onDropdownFilter,
  reportListing,
  handleDownloadExcelFile,
  itemPerPage,
  isLoading,
  handleRefreshList,
  isExcelLoading,
  disableShowLatestStatus = false,
}) => {
  const query = useQuery();
  const history = useHistory();
  const location = useLocation();
  const { t } = useTranslation();
  const { roles } = useSession();
  const page: any = query.get("page") || 1;

  const [filterState, setFilterState] = useState<any>({
    status: "",
    sortByCreatedAt: "DESC",
  });
  const { handleSearch } = useSearch({ onFilter, filterState, setFilterState });

  const tabKeyPaginationProps = {
    pathWithoutPage: {
      withoutTabKey: `/parcels-order-processing/reporting`,
    },
    pathWithPage: {
      withoutTabKey: `/parcels-order-processing/reporting?page=${page}`,
    },
  };

  const columns: any = [
    {
      title: "#",
      dataIndex: "index",
      render: (text: any, record: any, index: number) =>
        (page - 1) * itemPerPage + (index + 1),
    },
    {
      title: t("label.orderCode").toLocaleUpperCase(),
      dataIndex: ["data", "orderCode"],
      render: (orderCode: string, record: any) =>
        orderCode ? (
          <a
            className="clickable-id"
            onClick={(e: React.MouseEvent<HTMLElement>) => {
              e.stopPropagation();
              routeToOrderView(record.orderID, location, history);
            }}
          >
            {orderCode}
          </a>
        ) : (
          <span>{"-"}</span>
        ),
    },
    {
      title: t("label.plannedDeliveryDate").toLocaleUpperCase(),
      dataIndex: ["data", "deliveryDate"],
      render: (_: any, record: any) => {
        return (
          <Row className="margin-0-overwrite">
            {record?.data?.deliveryDate ? (
              <Col span={24}>{`${moment(record?.data?.deliveryDate).format(
                "DD-MM-YYYY",
              )} | ${moment(record?.data?.deliveryDate).format(
                "hh:mm a",
              )}`}</Col>
            ) : (
              <Col span={24}>-</Col>
            )}
          </Row>
        );
      },
    },
    {
      title: t("label.actualDeliveryDate").toLocaleUpperCase(),
      dataIndex: ["data", "actualDeliveryDate"],
      render: (text: any, record: any) => {
        if (text) {
          return (
            <span>
              {`${moment(record?.data?.deliveryDate).format(
                "DD-MM-YYYY",
              )} | ${moment(record?.data?.deliveryDate).format("hh:mm a")}`}
            </span>
          );
        }

        return <span>-</span>;
      },
    },
    {
      title: t("label.loadTime").toLocaleUpperCase(),
      dataIndex: "loadTime",
      render: (_: any, record: any) => {
        return (
          <Row className="margin-0-overwrite">
            {record?.data?.loadDateTime ? (
              <Col span={24}>{`${moment(record?.data?.loadDateTime).format(
                "DD-MM-YYYY",
              )} | ${moment(record?.data?.loadDateTime).format(
                "hh:mm a",
              )}`}</Col>
            ) : (
              <Col span={24}>-</Col>
            )}
          </Row>
        );
      },
    },
    {
      title: t("label.unloadTime").toLocaleUpperCase(),
      dataIndex: "unloadTime",
      render: (_: any, record: any) => {
        return (
          <Row className="margin-0-overwrite">
            {record?.data?.unloadDateTime ? (
              <Col span={24}>{`${moment(record?.data?.unloadDateTime).format(
                "DD-MM-YYYY",
              )} | ${moment(record?.data?.unloadDateTime).format(
                "hh:mm a",
              )}`}</Col>
            ) : (
              <Col span={24}>-</Col>
            )}
          </Row>
        );
      },
    },
    {
      title: t("label.trackingNo").toLocaleUpperCase(),
      dataIndex: ["data", "trackingNumber"],
    },
    {
      title: t("label.pickUpDateAndTime").toLocaleUpperCase(),
      dataIndex: ["firstMileInfoData", "pickupData", "pickupDate"],
      key: "pickUpDateAndTime",
      ellipsis: true,
      render: (text: any) =>
        text ? moment(text).format("DD-MM-YYYY hh:mm a") : null,
    },
    {
      title: t("label.customer").toLocaleUpperCase(),
      dataIndex: ["customerOrgData", "name"],
    },
    {
      title: t("label.journeyCode").toLocaleUpperCase(),
      dataIndex: ["trainPlanData", "journeyCode"],
    },
    {
      title: t("label.trolleyID").toLocaleUpperCase(),
      dataIndex: ["trolleyData", "code"],
    },
    {
      title: t("label.boxID").toLocaleUpperCase(),
      dataIndex: ["boxData", "code"],
    },
    {
      title: t("label.noOfParcels").toLocaleUpperCase(),
      dataIndex: ["data", "noOfParcel"],
    },
    {
      title: t("label.totalVolume").toLocaleUpperCase(),
      dataIndex: ["data", "totalVolume"],
    },
    {
      title: t("label.weight").toLocaleUpperCase(),
      dataIndex: ["data", "weight"],
    },
    {
      title: t("label.loadAt").toLocaleUpperCase(),
      dataIndex: ["loadAtData", "locationName"],
    },
    {
      title: t("label.unloadAt").toLocaleUpperCase(),
      dataIndex: ["unloadAtData", "locationName"],
    },
    {
      title: t("label.lastKnownLocation").toLocaleUpperCase(),
      dataIndex: ["lastKnownLocationData", "locationName"],
    },
    {
      title: t("label.status").toLocaleUpperCase(),
      dataIndex: ["data", "status"],
      render: (text: any, record: any) => {
        return (
          <span className="status-indicator" style={getStatusStyle(text)}>
            {text ? toCapitalisedSpaced(text) : ""}
          </span>
        );
      },
    },
    {
      title: t("label.estimatedCO2").toLocaleUpperCase() + " (gCO2)",
      dataIndex: ["data", "estimatedCO2"],
      render: (text: any) => text ?? "-",
    },
    {
      title: t("label.estimatedCO2Reduction").toLocaleUpperCase(),
      dataIndex: ["data", "estimatedCO2Reduction"],
      render: (text: any) => text ?? "-",
    },
    {
      title: t("label.estimatedRoadDistance").toLocaleUpperCase() + " (km)",
      dataIndex: ["data", "estimatedRoadDistance"],
      render: (text: any) => text ?? "-",
    },
    {
      title: t("label.estimatedRoadReductionDistance").toLocaleUpperCase(),
      dataIndex: ["data", "estimatedRoadReductionDistance"],
      render: (text: any) => text ?? "-",
    },
    {
      title: t("label.deliveryAttempts").toLocaleUpperCase(),
      dataIndex: ["returnInfo", "failedAttempt"],
    },
  ];

  const statusOptions = [{ label: t("option.all"), value: "" }].concat(
    map(orderStatusesMap, (status) => {
      return {
        label: ToTitleCase(status[roles[0].type].replaceAll("_", " ")),
        value: status[roles[0].type],
      };
    }),
  );

  const sortByCreatedAt = [
    { label: t("option.latest"), value: "DESC" },
    { label: t("option.earliest"), value: "ASC" },
  ];

  const handleFilterChange = (value: any, name: string) => {
    let selectedValue;

    switch (name) {
      case "dateFrom":
        selectedValue = value ? value.startOf("day").toISOString() : null;
        break;
      case "dateTo":
        selectedValue = value ? value.endOf("day").toISOString() : null;
        break;
      case "onlyLatestStatus":
        selectedValue = value ? "true" : "false"; // boolean will be cleared by the hook
        break;
      default:
        selectedValue = value;
    }

    if (Array.isArray(value)) {
      selectedValue = value.filter((item) => item !== "all");
    }

    setFilterState({ ...filterState, [name]: selectedValue });
  };

  return (
    <div>
      <div className="dm-report-sort-container">
        <ReportFilters
          handleFilterChange={handleFilterChange}
          filterState={filterState}
          handleDownloadExcelFile={handleDownloadExcelFile}
          excelType={"parcel"}
          reportListing={reportListing}
          statusOptions={statusOptions}
          sortByCreatedAt={sortByCreatedAt}
          handleRefreshList={handleRefreshList}
          isLoading={isLoading}
          isExcelLoading={isExcelLoading}
          disableShowLatestStatus={disableShowLatestStatus}
        />
        <Divider />
        <DataTableFilter
          searchPlaceholder={t("label.searchReportingParcels")}
          onSearch={(text: string) => handleSearch(text)}
        />
      </div>
      {isLoading ? (
        <div className="loading-container">
          <Spin size="large" indicator={<LoadingOutlined />} />
        </div>
      ) : (
          <DataTable
            rowKey={(record: any) => record._id}
            columns={columns}
            scroll={{ x: "max-content" }}
            dataSource={reportListing}
        />
      )}
    </div>
  );
};

export default ParcelReportList;
